<script setup>
import { ref, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useMapGetter } from 'dashboard/composables/store';
import Button from 'dashboard/components-next/button/Button.vue';

const emit = defineEmits(['apply', 'close']);

const { t } = useI18n();

const allLabels = useMapGetter('labels/getLabels');
const query = ref('');
const selectedLabels = ref([]);
const mode = ref('add');

const closeAndReset = () => {
  selectedLabels.value = [];
  query.value = '';
  mode.value = 'add';
  emit('close');
};

const filteredLabels = computed(() =>
  allLabels.value.filter(label =>
    label.title.toLowerCase().includes(query.value.toLowerCase())
  )
);

const isSelected = title => selectedLabels.value.includes(title);

const toggleLabel = title => {
  const idx = selectedLabels.value.indexOf(title);
  if (idx >= 0) selectedLabels.value.splice(idx, 1);
  else selectedLabels.value.push(title);
};

const switchMode = newMode => {
  mode.value = newMode;
  selectedLabels.value = [];
  query.value = '';
};

const onApply = () => {
  if (!selectedLabels.value.length) return;
  emit('apply', { labels: [...selectedLabels.value], mode: mode.value });
};
</script>

<template>
  <div
    v-on-clickaway="closeAndReset"
    class="fixed z-[9999] w-72 bg-n-solid-2 border border-solid border-n-strong rounded-xl shadow-xl flex flex-col overflow-hidden"
  >
    <!-- Header -->
    <div class="flex items-center justify-between px-3 pt-3 pb-2">
      <span class="text-sm font-semibold text-n-slate-12">
        {{ t('BULK_ACTION.CONTACTS.LABELS.ASSIGN_LABELS') }}
      </span>
      <button
        type="button"
        class="i-lucide-x size-4 text-n-slate-10 hover:text-n-slate-12 transition-colors"
        @click="closeAndReset"
      />
    </div>

    <!-- Mode tabs -->
    <div class="flex mx-3 mb-2 p-0.5 rounded-lg bg-n-solid-3 gap-0.5">
      <button
        v-for="tab in ['add', 'remove']"
        :key="tab"
        type="button"
        class="flex-1 text-xs py-1 px-2 rounded-md font-medium transition-all duration-150"
        :class="
          mode === tab
            ? 'bg-n-solid-2 text-n-slate-12 shadow-sm'
            : 'text-n-slate-10 hover:text-n-slate-11'
        "
        @click="switchMode(tab)"
      >
        {{
          tab === 'add'
            ? t('BULK_ACTION.CONTACTS.LABELS.MODE_ADD')
            : t('BULK_ACTION.CONTACTS.LABELS.MODE_REMOVE')
        }}
      </button>
    </div>

    <!-- Search -->
    <div class="relative mx-3 mb-2">
      <span
        class="absolute i-lucide-search size-3.5 top-1/2 -translate-y-1/2 ltr:left-3 rtl:right-3 text-n-slate-10 pointer-events-none"
      />
      <input
        v-model="query"
        type="search"
        :placeholder="t('BULK_ACTION.SEARCH_INPUT_PLACEHOLDER')"
        class="reset-base w-full h-8 py-2 px-6 ltr:pl-9 rtl:pr-9 ltr:pr-3 rtl:pl-3 text-sm focus:outline-none border-none rounded-lg bg-n-solid-3 text-n-slate-12"
      />
    </div>

    <!-- Label list -->
    <ul
      class="max-h-52 overflow-y-auto px-1 pb-1 m-0 list-none flex flex-col gap-0.5"
    >
      <li v-for="label in filteredLabels" :key="label.id">
        <button
          type="button"
          class="flex items-center gap-2.5 w-full rounded-lg px-2.5 py-1.5 text-left transition-colors duration-150"
          :class="
            isSelected(label.title) ? 'bg-n-solid-3' : 'hover:bg-n-solid-3'
          "
          @click="toggleLabel(label.title)"
        >
          <span
            class="size-3 rounded-sm flex-shrink-0 border border-solid border-n-weak"
            :style="{ backgroundColor: label.color }"
          />
          <span class="flex-1 text-sm text-n-slate-12 truncate">
            {{ label.title }}
          </span>
          <span
            v-if="isSelected(label.title)"
            class="i-lucide-check size-3.5 text-n-brand flex-shrink-0"
          />
        </button>
      </li>

      <li
        v-if="!filteredLabels.length"
        class="px-3 py-2 text-sm text-n-slate-10"
      >
        {{ t('BULK_ACTION.CONTACTS.LABELS.NO_LABELS_FOUND') }}
      </li>
    </ul>

    <!-- Footer -->
    <div class="p-2 border-t border-n-strong mt-1">
      <Button
        sm
        class="w-full"
        :color="mode === 'remove' ? 'ruby' : 'blue'"
        :label="
          mode === 'remove'
            ? t('BULK_ACTION.CONTACTS.LABELS.REMOVE_SELECTED_LABELS')
            : t('BULK_ACTION.CONTACTS.LABELS.ASSIGN_SELECTED_LABELS')
        "
        :disabled="!selectedLabels.length"
        @click="onApply"
      />
    </div>
  </div>
</template>
